import { ArrowDown, ArrowUp, HelpCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";

import type { DiscountCampaignAnalyticsShape } from "../../types/discount-analytics-types";

interface Props {
  campaign: DiscountCampaignAnalyticsShape;
}

function FormulaTooltip({
  description,
  formula,
  colorClass,
}: {
  description: string;
  formula: string;
  colorClass: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "shrink-0 inline-flex items-center justify-center rounded-full hover:bg-black/10 p-0.5 transition-colors",
            colorClass
          )}
          aria-label="Formula"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-xs space-y-1 text-xs p-3 bg-white border-2 border-slate-300 shadow-lg">
        <div className="text-slate-900 leading-relaxed whitespace-pre-line">{description}</div>
        <div className="mt-1 rounded bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-900 border border-slate-200">
          {formula}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ConfidenceBadge({
  confidence,
}: {
  confidence: DiscountCampaignAnalyticsShape["roiConfidence"];
}) {
  const { t } = useTranslation("reports");
  const variant: "secondary" | "default" | "destructive" =
    confidence === "HIGH" ? "default" : confidence === "MEDIUM" ? "secondary" : "destructive";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge variant={variant} className="flex items-center gap-1 font-semibold cursor-pointer">
          <Info className="h-3 w-3" />
          {t(`discounts.roi.confidence.${confidence}`)}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="max-w-xs text-xs p-3 bg-white border-2 border-slate-300 shadow-lg">
        <div className="text-slate-900 leading-relaxed">
          {t(`discounts.roi.confidenceTooltip.${confidence}`)}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function CampaignRoiCard({ campaign }: Props) {
  const { t } = useTranslation("reports");

  const incremental = campaign.incrementalRevenue;
  const incrementalPositive = incremental > 0;
  // "Net profit" (= Net revenue − Total costs) cannot be computed correctly
  // from the data we have — we lack COGS, operating costs, and tax. The
  // closest substitute (actualRevenue − totalDiscounted) double-counts the
  // discount because totalPrice is already post-discount. We intentionally
  // do NOT display a Net Profit tile until proper cost data lands; the row
  // below shows Net Revenue ("Doanh thu thực") for reference.
  const cannibalizationPercent = (campaign.cannibalizationRate * 100).toFixed(0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{t("discounts.roi.title")}</CardTitle>
            <CardDescription>
              {t("discounts.roi.description", { days: campaign.campaignDurationDays })}
            </CardDescription>
          </div>
          <ConfidenceBadge confidence={campaign.roiConfidence} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border-2 border-rose-200 bg-rose-100 p-3 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-rose-900">
                {t("discounts.roi.totalDiscounted")}
              </span>
              <FormulaTooltip
                description={t("discounts.roi.formulas.totalDiscounted.description")}
                formula={t("discounts.roi.formulas.totalDiscounted.formula")}
                colorClass="text-rose-700"
              />
            </div>
            <div className="mt-2 text-xl font-bold text-rose-700">
              <PriceDisplay amount={campaign.totalAmountDiscounted} />
            </div>
            <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-rose-700">
              {t("discounts.roi.cost")}
            </div>
          </div>

          <div
            className={cn(
              "rounded-lg border-2 p-3 shadow-sm",
              incrementalPositive
                ? "border-emerald-200 bg-emerald-100"
                : "border-amber-200 bg-amber-100"
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={cn(
                  "text-sm font-semibold",
                  incrementalPositive ? "text-emerald-900" : "text-amber-900"
                )}
              >
                {t("discounts.roi.incrementalRevenue")}
              </span>
              <FormulaTooltip
                description={t("discounts.roi.formulas.incrementalRevenue.description")}
                formula={t("discounts.roi.formulas.incrementalRevenue.formula")}
                colorClass={incrementalPositive ? "text-emerald-700" : "text-amber-700"}
              />
            </div>
            <div
              className={cn(
                "mt-2 text-xl font-bold flex items-center gap-1",
                incrementalPositive ? "text-emerald-700" : "text-amber-700"
              )}
            >
              {incrementalPositive ? (
                <ArrowUp className="h-4 w-4" />
              ) : (
                <ArrowDown className="h-4 w-4" />
              )}
              <PriceDisplay amount={Math.abs(incremental)} />
            </div>
            <div
              className={cn(
                "mt-2 text-[10px] font-bold uppercase tracking-wider",
                incrementalPositive ? "text-emerald-700" : "text-amber-700"
              )}
            >
              {t("discounts.roi.vsBaseline")}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <Row
            label={t("discounts.roi.actualRevenue")}
            value={<PriceDisplay amount={campaign.actualRevenue} />}
            tooltipDescription={t("discounts.roi.formulas.actualRevenue.description")}
            tooltipFormula={t("discounts.roi.formulas.actualRevenue.formula")}
          />
          <Row
            label={t("discounts.roi.baselineRevenue")}
            value={<PriceDisplay amount={campaign.baselineRevenue} />}
            extraNote={t("discounts.roi.baselineNote", {
              count: campaign.baselineAppointmentCount,
            })}
            tooltipDescription={t("discounts.roi.formulas.baselineRevenue.description")}
            tooltipFormula={t("discounts.roi.formulas.baselineRevenue.formula")}
          />
          <Row
            label={t("discounts.roi.cannibalization")}
            value={
              <span className="font-bold tabular-nums text-slate-900">
                {cannibalizationPercent}%
              </span>
            }
            extraNote={t("discounts.roi.cannibalizationNote")}
            tooltipDescription={t("discounts.roi.formulas.cannibalization.description")}
            tooltipFormula={t("discounts.roi.formulas.cannibalization.formula")}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  extraNote,
  tooltipDescription,
  tooltipFormula,
}: {
  label: string;
  value: React.ReactNode;
  extraNote?: string;
  tooltipDescription: string;
  tooltipFormula: string;
}) {
  return (
    <div className="rounded-md border-2 border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-900">{label}</span>
        <FormulaTooltip
          description={tooltipDescription}
          formula={tooltipFormula}
          colorClass="text-slate-600"
        />
      </div>
      <div className="mt-1 text-sm font-bold text-slate-900">{value}</div>
      {extraNote ? (
        <div className="mt-1 text-[10px] font-medium text-slate-700">{extraNote}</div>
      ) : null}
    </div>
  );
}
